import { useNavigate } from 'react-router';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useState } from 'react';
import PageContainer from '../layout/PageContainer';
import { FormFieldValue } from '../forms/FormFieldValue';
import { VendorFormState } from './VendorForm';
import { useMutation } from '@apollo/client/react';
import { ADD_VENDOR_MUTATION } from '../../mutations/VendorMutations';
import { GET_VENDORS_QUERY } from '../../queries/VendorQueries';
import VendorForm from './VendorForm';
import { validate as validateVendor } from '../../models/Vendor';

const INITIAL_FORM_VALUES: Partial<VendorFormState['values']> = {
  name: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  contactId: 0
};

export default function VendorCreate() {
  const navigate = useNavigate();

  const notifications = useNotifications();

  const [createVendor] = useMutation(
    ADD_VENDOR_MUTATION, {
    refetchQueries: [{ query: GET_VENDORS_QUERY }],
  });

  const [formState, setFormState] = useState<VendorFormState>(() => ({
    values: INITIAL_FORM_VALUES,
    errors: {},
  }));

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<VendorFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<VendorFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof VendorFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<VendorFormState['values']>) => {
        const { issues } = validateVendor(values);
        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const handleFormReset = useCallback(() => {
    setFormValues(INITIAL_FORM_VALUES);
  }, [setFormValues]);

  const handleFormSubmit = useCallback(async () => {
    const { issues } = validateVendor(formValues);
    if (issues && issues.length > 0) {
      setFormErrors(
        Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message])),
      );
      return;
    }
    setFormErrors({});

    try {
      const { data: result } = await createVendor({
        variables: {
          input: {
            id: 0,
            name: formValues.name || '',
            address: formValues.address || '',
            city: formValues.city || '',
            state: formValues.state || '',
            zip: formValues.zip || '',
            contactId: formValues.contactId || null
          }
        }
      });

      if (result?.added) {
        notifications.show('Vendor created successfully.', {
          severity: 'success',
          autoHideDuration: 3000,
        });

        navigate('/dashboard/vendors');
      } else {
        notifications.show(
          'Failed to create vendor. Reason: Unknown.',
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
      }
    } catch (createError) {
      notifications.show(
        `Failed to create vendor. Reason: ${(createError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw createError;
    }
  }, [createVendor, formValues, navigate, notifications, setFormErrors]);

  return (
    <PageContainer
      title="New Vendor"
      breadcrumbs={[{ title: 'Vendors', path: '/dashboard/vendors' }, { title: 'New' }]}
    >
      <VendorForm
        formState={formState}
        onFieldChange={handleFormFieldChange}
        onSubmit={handleFormSubmit}
        onReset={handleFormReset}
        submitButtonLabel="Create"
      />
    </PageContainer>
  );
}
